import { useMemo } from 'react';
import { toast } from 'sonner';
import { getBriefForStrategy } from '@shared/calculations/funding/projection/transcript/briefs';
import { renderTranscript } from '@shared/calculations/funding/projection/transcript/renderTranscript';

// Human-friendly strategy labels for the transcript title. Keyed off the
// strategy id stamped on `ClaimTerms` by each projection strategy.
const STRATEGY_DISPLAY_NAMES: Record<ProjectionStrategyId, string> = {
  'cm-two': 'CareMatters II',
  'oa-asset-care': 'OneAmerica Asset Care',
  'nyl-asset-flex': 'NYL Asset Flex',
  'lincoln-money-guard': 'Lincoln MoneyGuard',
  'traditional-ltc': 'Traditional LTC',
  'forecare': 'Global Atlantic ForeCare',
  'indexed-annuity-care': 'OneAmerica Indexed Annuity Care',
  'life-with-rider': 'Life Insurance with LTC Rider',
  'membership-program': 'CCAH Membership Program',
  'short-term-care': 'Short-Term Care Insurance',
  'hybrid-life-ira': 'Hybrid Life IRA (Asset Care III)',
  'generic-hybrid': 'Hybrid Life Insurance',
  'secure-care': 'Securian SecureCare',
};

// Builds the plain-text transcript for a projection and exposes a copy handler.
// The transcript is intentionally PII/PHI-free (no client name) so it can be
// pasted into external review tools; pure presentation logic lives in `@shared`.
export function useTranscript(projection: ClaimProjection) {
  const transcript = useMemo(() => {
    const { strategyId } = projection.terms;
    return renderTranscript(projection, {
      strategyDisplayName: STRATEGY_DISPLAY_NAMES[strategyId],
      brief: getBriefForStrategy(strategyId),
    });
  }, [projection]);

  const copy = (text: string) => {
    void navigator.clipboard.writeText(text).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Failed to copy'),
    );
  };

  return { transcript, copy };
}
